import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token() });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

const DEFAULT_SOFTWARE = [
  { name: 'Google Business Workspace', selected: true },
  { name: 'Microsoft O365', selected: true },
  { name: 'Google Drive (Desktop)', selected: true },
  { name: 'Zoom', selected: true },
  { name: 'DocuSign', selected: true },
  { name: 'Adobe Acrobat Pro', selected: true },
  { name: 'Java (for CalAters)', selected: true },
];

const BTN = ({ onClick, children, variant='default', style={}, disabled=false }) => {
  const base = { display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 18px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor: disabled ? 'not-allowed' : 'pointer', border:'1px solid', opacity: disabled ? 0.5 : 1 };
  const variants = {
    default: { background:'#fff', borderColor:'#E5E7EB', color:'#374151' },
    primary: { background: NAVY, borderColor: NAVY, color:'#fff' },
    orange:  { background: ORANGE, borderColor: ORANGE, color:'#fff' },
    green:   { background: '#10B981', borderColor: '#10B981', color:'#fff' },
    ghost:   { background:'transparent', borderColor:'transparent', color:'#6B7280' },
    danger:  { background:'#EF4444', borderColor:'#EF4444', color:'#fff' },
  };
  return <button onClick={onClick} disabled={disabled} style={{...base, ...variants[variant], ...style}}>{children}</button>;
};

const Badge = ({ status }) => {
  const map = {
    active:   { bg:'#FFFBEB', color:'#92400E', dot:'#F59E0B', label:'Pending' },
    pending:  { bg:'#FFFBEB', color:'#92400E', dot:'#F59E0B', label:'Pending' },
    complete: { bg:'#ECFDF5', color:'#065F46', dot:'#10B981', label:'Complete' },
    archived: { bg:'#F3F4F6', color:'#6B7280', dot:'#9CA3AF', label:'Archived' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'3px 10px', borderRadius:'12px', fontSize:'11px', fontWeight:'600', background:s.bg, color:s.color }}>
      <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:s.dot }}/>
      {s.label}
    </span>
  );
};

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ position:'fixed', bottom:'32px', left:'50%', transform:'translateX(-50%)', zIndex:9999,
      background:'#111827', color:'#fff', padding:'13px 24px', borderRadius:'10px',
      boxShadow:'0 8px 32px rgba(0,0,0,0.3)', fontSize:'13px', fontWeight:'500',
      display:'flex', alignItems:'center', gap:'10px', maxWidth:'500px', whiteSpace:'pre-line' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12"/></svg>
      {message}
    </div>
  );
}

function WizardProgressBar({ phase }) {
  const steps = ['New Hire Info', 'Hardware', 'Software', 'Review'];
  return (
    <div style={{ display:'flex', alignItems:'center', marginBottom:'32px' }}>
      {steps.map((step, i) => {
        const num = i + 1;
        const done = phase > num;
        const active = phase === num;
        return (
          <React.Fragment key={step}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' }}>
              <div style={{ width:'32px', height:'32px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'700', background: done ? '#10B981' : active ? NAVY : '#E5E7EB', color: done || active ? '#fff' : '#9CA3AF', transition:'all 0.2s' }}>
                {done ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg> : num}
              </div>
              <span style={{ fontSize:'11px', fontWeight: active ? '600' : '400', color: active ? NAVY : '#6B7280', whiteSpace:'nowrap' }}>{step}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex:1, height:'2px', background: done ? '#10B981' : '#E5E7EB', margin:'0 8px', marginBottom:'18px', transition:'all 0.2s' }}/>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MiniProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div style={{ marginTop:'12px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
        <span style={{ fontSize:'10px', color:'#9CA3AF' }}>Progress</span>
        <span style={{ fontSize:'10px', fontWeight:'600', color: pct === 100 ? '#10B981' : '#6B7280' }}>{completed}/{total} tasks</span>
      </div>
      <div style={{ height:'5px', background:'#E5E7EB', borderRadius:'3px', overflow:'hidden' }}>
        <div style={{ height:'100%', width: pct + '%', background: pct === 100 ? '#10B981' : NAVY, borderRadius:'3px', transition:'width 0.3s' }}/>
      </div>
    </div>
  );
}

function WizardModal({ onClose, onSave, staffList, officeList }) {
  const [phase, setPhase] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ first_name:'', last_name:'', personal_email:'', job_title:'', office_id:'', supervisor_id:'', start_date:'', notes:'' });
  const [hardware, setHardware] = useState([{ item_type:'Laptop', brand:'', model:'', is_apple:false, notes:'' }]);
  const [software, setSoftware] = useState(DEFAULT_SOFTWARE.map(s => ({...s})));

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inputStyle = { width:'100%', padding:'9px 11px', border:'1px solid #E5E7EB', borderRadius:'6px', fontSize:'13px', color:'#111827', outline:'none', boxSizing:'border-box' };
  const labelStyle = { display:'block', fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.04em' };

  const addHardware = () => setHardware(h => [...h, { item_type:'', brand:'', model:'', is_apple:false, notes:'' }]);
  const removeHardware = (i) => setHardware(h => h.filter((_, idx) => idx !== i));
  const setHW = (i, k, v) => setHardware(h => h.map((item, idx) => idx === i ? {...item, [k]: v} : item));

  const validatePhase = () => {
    if (phase === 1) {
      if (!form.first_name.trim()) { setError('First name is required.'); return false; }
      if (!form.last_name.trim())  { setError('Last name is required.'); return false; }
      if (!form.personal_email.trim()) { setError('Personal email is required.'); return false; }
      // Supervisor is mandatory: time-off requests + notifications depend on it being set.
      if (!form.supervisor_id) { setError('Supervisor is required — the new hire\'s time-off requests will route to this person.'); return false; }
    }
    if (phase === 2) {
      if (hardware.some(h => !h.item_type.trim())) { setError('All hardware items need a type.'); return false; }
    }
    setError(''); return true;
  };

  const next = () => { if (validatePhase()) setPhase(p => p + 1); };
  const back = () => { setError(''); setPhase(p => p - 1); };

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(API + '/onboarding', { ...form, hardware, software }, { headers: headers() });
      onSave();
    } catch (e) { setError(e.response?.data?.error || 'Failed to create onboarding case.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:'14px', padding:'36px', width:'660px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'28px' }}>
          <div>
            <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#111827', margin:0 }}>New Employee Onboarding</h2>
            <p style={{ fontSize:'12px', color:'#9CA3AF', margin:'4px 0 0 0' }}>Complete all steps to initiate the onboarding process</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'22px', lineHeight:1 }}>×</button>
        </div>
        <WizardProgressBar phase={phase} />
        {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'6px', padding:'10px 14px', marginBottom:'18px', fontSize:'12px', color:'#B91C1C' }}>{error}</div>}

        {phase === 1 && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
            <div><label style={labelStyle}>First Name *</label><input value={form.first_name} onChange={e => set('first_name', e.target.value)} style={inputStyle} placeholder="First name"/></div>
            <div><label style={labelStyle}>Last Name *</label><input value={form.last_name} onChange={e => set('last_name', e.target.value)} style={inputStyle} placeholder="Last name"/></div>
            <div style={{ gridColumn:'1 / -1' }}>
              <label style={labelStyle}>Personal Email * <span style={{ color:'#9CA3AF', fontWeight:'400', textTransform:'none' }}>(for welcome letter)</span></label>
              <input type="email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} style={inputStyle} placeholder="personal@email.com"/>
            </div>
            <div style={{ gridColumn:'1 / -1' }}><label style={labelStyle}>Job Title</label><input value={form.job_title} onChange={e => set('job_title', e.target.value)} style={inputStyle} placeholder="e.g. Collections Manager"/></div>
            <div>
              <label style={labelStyle}>Office Location</label>
              <select value={form.office_id} onChange={e => set('office_id', e.target.value)} style={inputStyle}>
                <option value="">Select office...</option>
                {officeList.map(o => <option key={o.id} value={o.id}>{o.office_number}{o.office_name && o.office_name !== o.office_number ? ' — ' + o.office_name : ''}{!o.staff_id ? ' (Unoccupied)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Manager / Supervisor *</label>
              <select value={form.supervisor_id} onChange={e => set('supervisor_id', e.target.value)} style={inputStyle} required>
                <option value="">Select supervisor...</option>
                {staffList.filter(s => s.is_supervisor).map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Start Date</label><input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inputStyle}/></div>
            <div><label style={labelStyle}>Notes</label><input value={form.notes} onChange={e => set('notes', e.target.value)} style={inputStyle} placeholder="Optional notes"/></div>
          </div>
        )}

        {phase === 2 && (
          <div>
            <p style={{ fontSize:'13px', color:'#6B7280', marginBottom:'20px' }}>Specify the hardware this employee will need. Check the Apple box if any item is Apple — this will add an Apple Business Manager task for IT.</p>
            {hardware.map((h, i) => (
              <div key={i} style={{ border:'1px solid #E5E7EB', borderRadius:'8px', padding:'16px', marginBottom:'12px', background:'#FAFAFA' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                  <span style={{ fontSize:'12px', fontWeight:'600', color:NAVY }}>Item {i + 1}</span>
                  {hardware.length > 1 && <button onClick={() => removeHardware(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'#EF4444', fontSize:'18px', lineHeight:1 }}>×</button>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' }}>
                  <div><label style={labelStyle}>Type *</label>
                    <select value={h.item_type} onChange={e => setHW(i, 'item_type', e.target.value)} style={inputStyle}>
                      <option value="">Select...</option>
                      {['Laptop','Desktop','Monitor','iPad','iPhone','Keyboard','Mouse','Docking Station','Webcam','Headset','Other'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Brand</label><input value={h.brand} onChange={e => setHW(i, 'brand', e.target.value)} style={inputStyle} placeholder="e.g. Apple, HP"/></div>
                  <div><label style={labelStyle}>Model</label><input value={h.model} onChange={e => setHW(i, 'model', e.target.value)} style={inputStyle} placeholder="e.g. MacBook Pro"/></div>
                </div>
                <div style={{ marginTop:'10px', display:'flex', alignItems:'center', gap:'8px' }}>
                  <input type="checkbox" id={'apple-'+i} checked={h.is_apple} onChange={e => setHW(i, 'is_apple', e.target.checked)} style={{ accentColor: ORANGE, width:'14px', height:'14px' }}/>
                  <label htmlFor={'apple-'+i} style={{ fontSize:'12px', color:'#374151', cursor:'pointer' }}>Apple device — add to Apple Business Manager</label>
                </div>
              </div>
            ))}
            <BTN onClick={addHardware} style={{ marginTop:'4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Another Item
            </BTN>
          </div>
        )}

        {phase === 3 && (
          <div>
            <p style={{ fontSize:'13px', color:'#6B7280', marginBottom:'20px' }}>All software is selected by default. Uncheck any items this employee does not need.</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              {software.map((s, i) => (
                <label key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 14px', border:'1px solid ' + (s.selected ? NAVY : '#E5E7EB'), borderRadius:'8px', cursor:'pointer', background: s.selected ? '#F0F4FF' : '#fff', transition:'all 0.15s' }}>
                  <input type="checkbox" checked={s.selected} onChange={e => setSoftware(sw => sw.map((item, idx) => idx === i ? {...item, selected: e.target.checked} : item))} style={{ accentColor: NAVY, width:'14px', height:'14px', flexShrink:0 }}/>
                  <span style={{ fontSize:'13px', fontWeight: s.selected ? '600' : '400', color: s.selected ? NAVY : '#6B7280' }}>{s.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {phase === 4 && (
          <div>
            <div style={{ background:'#F9FAFB', borderRadius:'8px', padding:'20px', marginBottom:'16px' }}>
              <h3 style={{ fontSize:'13px', fontWeight:'700', color:NAVY, margin:'0 0 12px 0', textTransform:'uppercase', letterSpacing:'0.04em' }}>New Hire</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', fontSize:'13px' }}>
                <div><span style={{ color:'#6B7280' }}>Name: </span><strong>{form.first_name} {form.last_name}</strong></div>
                <div><span style={{ color:'#6B7280' }}>Email: </span><strong>{form.personal_email}</strong></div>
                {form.job_title && <div><span style={{ color:'#6B7280' }}>Title: </span><strong>{form.job_title}</strong></div>}
                <div><span style={{ color:'#6B7280' }}>Office: </span><strong>{officeList.find(o => o.id == form.office_id)?.office_number || '—'}</strong></div>
                <div><span style={{ color:'#6B7280' }}>Manager: </span><strong>{staffList.find(s => s.id == form.supervisor_id) ? staffList.find(s => s.id == form.supervisor_id).first_name + ' ' + staffList.find(s => s.id == form.supervisor_id).last_name : '—'}</strong></div>
                <div><span style={{ color:'#6B7280' }}>Start Date: </span><strong>{form.start_date || '—'}</strong></div>
              </div>
            </div>
            <div style={{ background:'#F9FAFB', borderRadius:'8px', padding:'20px', marginBottom:'16px' }}>
              <h3 style={{ fontSize:'13px', fontWeight:'700', color:NAVY, margin:'0 0 12px 0', textTransform:'uppercase', letterSpacing:'0.04em' }}>Hardware ({hardware.length} items)</h3>
              {hardware.map((h, i) => <div key={i} style={{ fontSize:'13px', color:'#374151', marginBottom:'4px' }}>• {h.item_type}{h.brand ? ' — ' + h.brand : ''}{h.model ? ' ' + h.model : ''}{h.is_apple ? ' 🍎' : ''}</div>)}
            </div>
            <div style={{ background:'#F9FAFB', borderRadius:'8px', padding:'20px' }}>
              <h3 style={{ fontSize:'13px', fontWeight:'700', color:NAVY, margin:'0 0 12px 0', textTransform:'uppercase', letterSpacing:'0.04em' }}>Software ({software.filter(s => s.selected).length} selected)</h3>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {software.filter(s => s.selected).map(s => <span key={s.name} style={{ padding:'3px 10px', background:'#EFF6FF', color:'#1E40AF', borderRadius:'10px', fontSize:'12px', fontWeight:'500' }}>{s.name}</span>)}
              </div>
            </div>
            <div style={{ marginTop:'16px', padding:'14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'8px', fontSize:'12px', color:'#92400E' }}>
              <strong>On submit:</strong> IT and HR will be notified in their sidebar notification panel to complete their assigned tasks.
            </div>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'space-between', marginTop:'28px' }}>
          <div>{phase > 1 && <BTN onClick={back}>← Back</BTN>}</div>
          <div style={{ display:'flex', gap:'8px' }}>
            <BTN onClick={onClose}>Cancel</BTN>
            {phase < 4 && <BTN variant="primary" onClick={next}>Next →</BTN>}
            {phase === 4 && <BTN variant="orange" onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Advance'}</BTN>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CaseDetail({ caseData, onClose, onUpdate, currentUser, onToast }) {
  const [tasks, setTasks] = useState(caseData.tasks || []);
  const [advancing, setAdvancing] = useState(false);
  const [provBusy, setProvBusy] = useState(null);
  const [provMsg, setProvMsg] = useState({});

  useEffect(() => { setTasks(caseData.tasks || []); }, [caseData]);

  const completeTask = async (taskId) => {
    if (tasks.find(t => t.id === taskId)?.completed) return;
    try {
      await axios.patch(API + '/onboarding/tasks/' + taskId + '/complete', {}, { headers: headers() });
      setTasks(t => t.map(task => task.id === taskId ? {...task, completed: true} : task));
    } catch (e) { alert('Failed to complete task: ' + (e.response?.data?.error || e.message)); }
  };

  const PROVISION_SUFFIX = { provision_ad: 'ad', provision_workspace: 'workspace', provision_portal: 'portal' };
  const runProvision = async (task) => {
    const suffix = PROVISION_SUFFIX[task.action_key];
    if (!suffix) return;
    setProvBusy(task.id);
    setProvMsg(m => ({ ...m, [task.id]: null }));
    try {
      const res = await axios.post(API + '/onboarding/' + caseData.id + '/provision/' + suffix, {}, { headers: headers() });
      setProvMsg(m => ({ ...m, [task.id]: { ok: true, text: res.data.message || 'Done.' } }));
      setTasks(ts => ts.map(x => x.id === task.id ? { ...x, completed: true } : x));
    } catch (e) {
      setProvMsg(m => ({ ...m, [task.id]: { ok: false, text: e.response?.data?.error || e.message } }));
    } finally {
      setProvBusy(null);
    }
  };

  const itTasks  = tasks.filter(t => t.assigned_to === 9);
  const hrTasks  = tasks.filter(t => t.assigned_to === 58);
  const jonTasks = tasks.filter(t => t.assigned_to === 15);
  const completed = tasks.filter(t => t.completed).length;
  const progress = tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;

  const isInitiator = currentUser && caseData.created_by === currentUser.id;
  const isIT = currentUser?.username === 'memmetti';
  const isHR = currentUser?.username === 'hsaeed';
  // Admins can check any task and drive the full workflow (run-through / oversight).
  const isAdmin = currentUser?.role === 'admin';
  const canIT  = isIT || isAdmin;
  const canHR  = isHR || isAdmin;
  const canJon = isInitiator || isAdmin;

  const itAllDone  = itTasks.length > 0 && itTasks.every(t => t.completed);
  const hrAllDone  = hrTasks.length > 0 && hrTasks.every(t => t.completed);
  const jonAllDone = jonTasks.length > 0 && jonTasks.every(t => t.completed);

  const showAdvance  = (canIT && itAllDone && !jonAllDone) || (canHR && hrAllDone && !jonAllDone);
  const showComplete = canJon && jonAllDone;

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      const res = await axios.patch(API + '/onboarding/' + caseData.id + '/advance', {}, { headers: headers() });
      onToast(res.data.message || 'Case advanced successfully.');
      onUpdate();
    } catch (e) { alert('Failed to advance: ' + (e.response?.data?.error || e.message)); }
    finally { setAdvancing(false); }
  };


  const handleComplete = async () => {
    setAdvancing(true);
    try {
      const res = await axios.post(API + '/email/send-welcome', { case_id: caseData.id }, { headers: headers() });
      onToast(res.data.message || 'Welcome email sent successfully!');
      onUpdate();
      onClose();
    } catch (e) {
      alert('Failed to send welcome email: ' + (e.response?.data?.error || e.message));
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:'14px', padding:'36px', width:'700px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
          <div>
            <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#111827', margin:0 }}>{caseData.first_name} {caseData.last_name}</h2>
            <p style={{ fontSize:'12px', color:'#9CA3AF', margin:'4px 0 0 0' }}>
              {caseData.job_title && <span>{caseData.job_title} · </span>}
              Case #{caseData.id} · Started {new Date(caseData.created_at).toLocaleDateString()}
            </p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <Badge status={caseData.status}/>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'22px', lineHeight:1 }}>×</button>
          </div>
        </div>

        {/* Overall progress */}
        <div style={{ marginBottom:'24px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
            <span style={{ fontSize:'12px', fontWeight:'600', color:'#374151' }}>Overall Progress</span>
            <span style={{ fontSize:'12px', color:'#6B7280' }}>{completed}/{tasks.length} tasks complete</span>
          </div>
          <div style={{ height:'8px', background:'#E5E7EB', borderRadius:'4px', overflow:'hidden' }}>
            <div style={{ height:'100%', width: progress + '%', background: progress === 100 ? '#10B981' : NAVY, borderRadius:'4px', transition:'width 0.3s' }}/>
          </div>
        </div>

        {/* Workflow phase indicators */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'24px' }}>
          {[
            { label:'Jon — Initiated',      done: true },
            { label:'IT — Setup',           done: itAllDone },
            { label:'HR — Credentials',     done: hrAllDone },
            { label:'Jon — Welcome Email',  done: caseData.status === 'complete' },
          ].map((step, i) => (
            <div key={i} style={{ flex:1, textAlign:'center', padding:'6px 4px', borderRadius:'6px', background: step.done ? '#ECFDF5' : '#F9FAFB', border:'1px solid ' + (step.done ? '#BBF7D0' : '#E5E7EB') }}>
              <div style={{ fontSize:'10px', fontWeight:'600', color: step.done ? '#065F46' : '#9CA3AF' }}>{step.done ? '✓ ' : ''}{step.label}</div>
            </div>
          ))}
        </div>

        {/* Info grid */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'24px' }}>
          {[
            ['Office', caseData.office_number || '—'],
            ['Manager', caseData.supervisor_first ? caseData.supervisor_first + ' ' + caseData.supervisor_last : '—'],
            ['Start Date', caseData.start_date ? new Date(caseData.start_date).toLocaleDateString() : '—'],
            ['Personal Email', caseData.personal_email || '—'],
            ['First Login', caseData.first_login ? '✓ Logged in' : 'Not yet'],
          ].map(([label, value]) => (
            <div key={label} style={{ background:'#F9FAFB', borderRadius:'6px', padding:'12px' }}>
              <div style={{ fontSize:'10px', fontWeight:'600', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'4px' }}>{label}</div>
              <div style={{ fontSize:'13px', fontWeight:'600', color:'#111827' }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Hardware */}
        {caseData.hardware?.length > 0 && (
          <div style={{ marginBottom:'20px' }}>
            <h3 style={{ fontSize:'12px', fontWeight:'700', color:NAVY, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>Hardware</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
              {caseData.hardware.map((h, i) => <span key={i} style={{ padding:'4px 10px', background:'#F3F4F6', borderRadius:'6px', fontSize:'12px', color:'#374151' }}>{h.item_type}{h.brand ? ' · ' + h.brand : ''}{h.model ? ' ' + h.model : ''}{h.is_apple ? ' 🍎' : ''}</span>)}
            </div>
          </div>
        )}

        {/* Software */}
        {caseData.software?.length > 0 && (
          <div style={{ marginBottom:'20px' }}>
            <h3 style={{ fontSize:'12px', fontWeight:'700', color:NAVY, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>Software</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
              {caseData.software.filter(s => s.selected).map((s, i) => <span key={i} style={{ padding:'4px 10px', background:'#EFF6FF', color:'#1E40AF', borderRadius:'6px', fontSize:'12px' }}>{s.software_name}</span>)}
            </div>
          </div>
        )}

        {/* IT Tasks */}
        <div style={{ marginBottom:'16px' }}>
          <h3 style={{ fontSize:'12px', fontWeight:'700', color:NAVY, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>
            IT Tasks — Michael Emmetti
            {itAllDone && <span style={{ marginLeft:'8px', color:'#10B981', fontWeight:'600' }}>✓ Complete</span>}
          </h3>
          {itTasks.map(t => {
            const provisionable = !!PROVISION_SUFFIX[t.action_key];
            const msg = provMsg[t.id];
            return (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'6px', background: t.completed ? '#F0FDF4' : '#F9FAFB', marginBottom:'6px', border:'1px solid ' + (t.completed ? '#BBF7D0' : '#E5E7EB'), flexWrap:'wrap' }}>
              <input type="checkbox" checked={t.completed} onChange={() => completeTask(t.id)} disabled={t.completed || !canIT} style={{ accentColor:'#10B981', width:'15px', height:'15px', cursor: (t.completed || !canIT) ? 'default' : 'pointer' }}/>
              <span style={{ fontSize:'13px', color: t.completed ? '#6B7280' : '#111827', textDecoration: t.completed ? 'line-through' : 'none', flex:1 }}>{t.title}</span>
              {provisionable && !t.completed && (
                <BTN variant="primary" onClick={() => runProvision(t)} disabled={provBusy === t.id || !canIT} style={{ padding:'5px 12px', fontSize:'12px' }}>
                  {provBusy === t.id ? 'Working…' : 'Create'}
                </BTN>
              )}
              {t.completed && <span style={{ fontSize:'11px', color:'#10B981', fontWeight:'600' }}>Done</span>}
              {msg && <div style={{ flexBasis:'100%', fontSize:'11px', color: msg.ok ? '#065F46' : '#9B3A1F', paddingLeft:'25px', marginTop:'2px' }}>{msg.text}</div>}
            </div>
            );
          })}
        </div>

        {/* HR Tasks */}
        <div style={{ marginBottom:'16px' }}>
          <h3 style={{ fontSize:'12px', fontWeight:'700', color:NAVY, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>
            HR Tasks — Haseeb Saeed
            {hrAllDone && <span style={{ marginLeft:'8px', color:'#10B981', fontWeight:'600' }}>✓ Complete</span>}
          </h3>
          {hrTasks.map(t => (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'6px', background: t.completed ? '#F0FDF4' : '#F9FAFB', marginBottom:'6px', border:'1px solid ' + (t.completed ? '#BBF7D0' : '#E5E7EB') }}>
              <input type="checkbox" checked={t.completed} onChange={() => completeTask(t.id)} disabled={t.completed || !canHR} style={{ accentColor:'#10B981', width:'15px', height:'15px', cursor: (t.completed || !canHR) ? 'default' : 'pointer' }}/>
              <span style={{ fontSize:'13px', color: t.completed ? '#6B7280' : '#111827', textDecoration: t.completed ? 'line-through' : 'none', flex:1 }}>{t.title}</span>
              {t.completed && <span style={{ fontSize:'11px', color:'#10B981', fontWeight:'600' }}>Done</span>}
            </div>
          ))}
        </div>

        {/* Jon / Administration Tasks */}
        {jonTasks.length > 0 && (
          <div style={{ marginBottom:'24px' }}>
            <h3 style={{ fontSize:'12px', fontWeight:'700', color:NAVY, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'10px' }}>
              Administration — Steven Foley
              {jonAllDone && <span style={{ marginLeft:'8px', color:'#10B981', fontWeight:'600' }}>✓ Complete</span>}
            </h3>
            {jonTasks.map(t => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'6px', background: t.completed ? '#F0FDF4' : '#F9FAFB', marginBottom:'6px', border:'1px solid ' + (t.completed ? '#BBF7D0' : '#E5E7EB') }}>
                <input type="checkbox" checked={t.completed} onChange={() => completeTask(t.id)} disabled={t.completed || !canJon} style={{ accentColor:'#10B981', width:'15px', height:'15px', cursor: (t.completed || !canJon) ? 'default' : 'pointer' }}/>
                <span style={{ fontSize:'13px', color: t.completed ? '#6B7280' : '#111827', textDecoration: t.completed ? 'line-through' : 'none', flex:1 }}>{t.title}</span>
                {t.completed && <span style={{ fontSize:'11px', color:'#10B981', fontWeight:'600' }}>Done</span>}
              </div>
            ))}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
          <BTN onClick={onClose}>Close</BTN>
          {showAdvance && (
            <BTN variant="primary" onClick={handleAdvance} disabled={advancing}>
              {advancing ? 'Advancing...' : 'Advance →'}
            </BTN>
          )}
          {showComplete && (
            <BTN variant="orange" onClick={handleComplete} disabled={advancing}>
              Complete &amp; Send Welcome Email
            </BTN>
          )}
        </div>
      </div>
    </div>
  );
}

function CaseCard({ c, onClick, onOpenChat, isAdmin, isSelected, onSelect }) {
  const taskCount = parseInt(c.task_count) || 0;
  const tasksCompleted = parseInt(c.tasks_completed) || 0;
  return (
    <div onClick={onClick} style={{ border:'1px solid ' + (isSelected ? '#6366F1' : '#E5E7EB'), borderRadius:'10px', padding:'18px', background: isSelected ? '#F5F3FF' : '#fff', cursor:'pointer', transition:'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'8px' }}>
        <div>
          <div style={{ fontSize:'14px', fontWeight:'700', color:'#111827' }}>{c.first_name} {c.last_name}</div>
          {c.job_title && <div style={{ fontSize:'11px', color:'#6B7280', marginTop:'1px' }}>{c.job_title}</div>}
          <div style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'2px' }}>Started {new Date(c.created_at).toLocaleDateString()}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          {isAdmin && (
            <input type="checkbox" checked={isSelected || false}
              onChange={e => { e.stopPropagation(); onSelect(c.id); }}
              onClick={e => e.stopPropagation()}
              style={{ width:'15px', height:'15px', accentColor:'#6366F1', cursor:'pointer' }}
            />
          )}
          <Badge status={c.status}/>
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'4px', fontSize:'12px', color:'#6B7280', marginBottom:'4px' }}>
        <span>📍 Location: {c.office_number || '—'}</span>
        <span style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          👤 Supervisor:&nbsp;
          {c.supervisor_first ? (
            <button onClick={e => { e.stopPropagation(); if(onOpenChat) onOpenChat(c.supervisor_first + ' ' + c.supervisor_last); }}
              title={'Chat with ' + c.supervisor_first + ' ' + c.supervisor_last}
              style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'inline-flex', alignItems:'center', gap:'4px', color:'#1A3A5C', fontWeight:'600', fontSize:'12px', textDecoration:'underline', textDecorationStyle:'dotted' }}
              onMouseEnter={e => e.currentTarget.style.color='#EA6F2E'}
              onMouseLeave={e => e.currentTarget.style.color='#1A3A5C'}>
              {c.supervisor_first + ' ' + c.supervisor_last}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </button>
          ) : '—'}
        </span>
      </div>
      {taskCount > 0 && <MiniProgressBar completed={tasksCompleted} total={taskCount}/>}
      {c.first_login && <div style={{ marginTop:'8px', fontSize:'11px', color:'#10B981', fontWeight:'600' }}>✓ First login completed</div>}
    </div>
  );
}

export default function Onboarding({ isAdmin, currentUser, openCaseId, onCaseOpened, onOpenChat }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [officeList, setOfficeList] = useState([]);
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const clearSelected = () => setSelectedIds([]);

  const handleArchive = async () => {
    try {
      await axios.patch(API + '/onboarding/archive', { ids: selectedIds }, { headers: headers() });
      setSelectedIds([]);
      fetchAll();
      setToast(selectedIds.length + ' case' + (selectedIds.length > 1 ? 's' : '') + ' archived.');
    } catch (e) { alert('Failed to archive.'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(API + '/onboarding/delete', { data: { ids: selectedIds }, headers: headers() });
      setSelectedIds([]);
      setConfirmDelete(false);
      fetchAll();
      setToast(selectedIds.length + ' case' + (selectedIds.length > 1 ? 's' : '') + ' deleted.');
    } catch (e) { alert('Failed to delete.'); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const id = openCaseId?.value || openCaseId;
    if (id) {
      axios.get(API + '/onboarding/' + parseInt(id), { headers: headers() })
        .then(res => {
          setSelectedCase(res.data);
          if (onCaseOpened) onCaseOpened();
        })
        .catch(e => console.error('Deep-link failed:', e));
    }
  }, [openCaseId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [casesRes, staffRes, officesRes] = await Promise.all([
        axios.get(API + '/onboarding', { headers: headers() }),
        axios.get(API + '/staff', { headers: headers() }),
        axios.get(API + '/offices', { headers: headers() }),
      ]);
      setCases(casesRes.data);
      setStaffList(staffRes.data);
      setOfficeList(officesRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCase = async (c) => {
    try {
      const res = await axios.get(API + '/onboarding/' + c.id, { headers: headers() });
      setSelectedCase(res.data);
    } catch (e) { console.error(e); }
  };

  const pending  = cases.filter(c => c.status !== 'complete' && c.status !== 'archived');
  const complete = cases.filter(c => c.status === 'complete');

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'300px', gap:'10px' }}>
      <div style={{ width:'18px', height:'18px', border:'2px solid #E0E7FF', borderTopColor: NAVY, borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
      <span style={{ color:'#6B7280', fontSize:'13px' }}>Loading...</span>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  return (
    <div style={{ padding:'24px 28px' }}>
      {showWizard && <WizardModal staffList={staffList} officeList={officeList} onClose={() => setShowWizard(false)} onSave={() => { setShowWizard(false); fetchAll(); }}/>}
      {toast && <Toast message={toast} onDone={() => setToast(null)}/>}
      {selectedCase && <CaseDetail caseData={selectedCase} onClose={() => setSelectedCase(null)} onUpdate={fetchAll} currentUser={currentUser} onToast={setToast}/>}


      {confirmDelete && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:'12px', padding:'32px', width:'420px', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ width:'48px', height:'48px', borderRadius:'50%', background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 style={{ fontSize:'16px', fontWeight:'700', color:'#111827', textAlign:'center', margin:'0 0 8px 0' }}>Delete {selectedIds.length} Case{selectedIds.length > 1 ? 's' : ''}?</h3>
            <p style={{ fontSize:'13px', color:'#6B7280', textAlign:'center', margin:'0 0 24px 0' }}>
              This will permanently delete {selectedIds.length === 1 ? 'this onboarding case' : 'these ' + selectedIds.length + ' onboarding cases'} and all associated tasks. This cannot be undone.
            </p>
            <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
              <BTN onClick={() => setConfirmDelete(false)}>Cancel</BTN>
              <BTN variant="danger" onClick={handleDelete}>Yes, Delete</BTN>
            </div>
          </div>
        </div>
      )}

      {isAdmin && selectedIds.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 16px', background:'#F0F4FF', border:'1px solid #C7D7F9', borderRadius:'8px', marginBottom:'16px' }}>
          <span style={{ fontSize:'13px', fontWeight:'600', color:NAVY }}>{selectedIds.length} selected</span>
          <div style={{ flex:1 }}/>
          <BTN onClick={clearSelected}>Deselect All</BTN>
          <BTN onClick={handleArchive}>Archive</BTN>
          <BTN variant="danger" onClick={() => setConfirmDelete(true)}>Delete</BTN>
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
        <div>
          <h2 style={{ fontSize:'16px', fontWeight:'700', color:'#111827', margin:'0 0 2px 0' }}>Employee Onboarding</h2>
          <p style={{ fontSize:'12px', color:'#9CA3AF', margin:0 }}>{pending.length} pending · {complete.length} complete</p>
        </div>
        <BTN variant="orange" onClick={() => setShowWizard(true)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Employee
        </BTN>
      </div>

      {cases.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px', color:'#9CA3AF' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ marginBottom:'16px' }}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
          <p style={{ fontSize:'14px', fontWeight:'600', color:'#6B7280', marginBottom:'8px' }}>No onboarding cases yet</p>
          <p style={{ fontSize:'13px', marginBottom:'20px' }}>Click New Employee to start the onboarding process</p>
          <BTN variant="orange" onClick={() => setShowWizard(true)}>New Employee</BTN>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <h3 style={{ fontSize:'12px', fontWeight:'700', color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'12px' }}>Pending ({pending.length})</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'12px', marginBottom:'28px' }}>
                {pending.map(c => <CaseCard key={c.id} c={c} onClick={() => openCase(c)} onOpenChat={onOpenChat} isAdmin={isAdmin} isSelected={selectedIds.includes(c.id)} onSelect={toggleSelect}/>)}
              </div>
            </>
          )}
          {complete.length > 0 && (
            <>
              <h3 style={{ fontSize:'12px', fontWeight:'700', color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'12px' }}>Completed ({complete.length})</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'12px' }}>
                {complete.map(c => <CaseCard key={c.id} c={c} onClick={() => openCase(c)} onOpenChat={onOpenChat} isAdmin={isAdmin} isSelected={selectedIds.includes(c.id)} onSelect={toggleSelect}/>)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
